import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError, finalize, map, tap } from "rxjs/operators";
import { LoaderService } from "./services/loader.service";

const httpOptions: { headers; observe } = {
  headers: new HttpHeaders({
    "Content-Type": "application/json",
  }),
  observe: "response",
};
const httpOptionsWithParams: { headers; observe; params } = {
  headers: new HttpHeaders({
    "Content-Type": "application/json",
  }),
  observe: "response",
  params: {},
};

const csvHttpOptions: { headers; responseType; observe } = {
  headers: new HttpHeaders({
    "Content-Type": "text/csv",
    Accept: "text/csv",
  }),
  responseType: "text/csv",
  observe: "response",
};

const fileHttpOptions: { headers; responseType; observe } = {
  headers: new HttpHeaders({
    "Content-Type": "application/json",
  }),
  responseType: "blob",
  observe: "response",
};

@Injectable({
  providedIn: "root",
})
export class HttpService {
  constructor(private http: HttpClient, private loaderService: LoaderService) {}

  getCSV<T>(url: string): Observable<T | ArrayBuffer> {
    this.loaderService.show();
    return this.http.get(url, csvHttpOptions).pipe(
      tap((data) => console.log("HttpService GET:", data)),
      finalize(() => this.loaderService.hide())
    );
  }

  get<T>(url: string, showLoader: boolean = true): Observable<T> {
    if (showLoader) this.loaderService.show();
    return this.http.get(url, httpOptions).pipe(
      map((data: HttpResponse<any>) => data.body),
      tap((data) => console.log("HttpService GET:", data)),
      finalize(() => (showLoader ? this.loaderService.hide() : undefined))
    );
  }

  getWithParams<T>(url: string, params: object): Observable<T> {
    var tempHttpOptions = httpOptionsWithParams;
    tempHttpOptions.params = params;
    this.loaderService.show();
    return this.http.get(url, tempHttpOptions).pipe(
      map((data: HttpResponse<any>) => data.body),
      tap((data) => console.log("HttpService GET:", data)),
      finalize(() => this.loaderService.hide())
    );
  }

  getBlob(
    url: string
  ): Observable<{ fileName: string; fileType: string; data: Blob }> {
    this.loaderService.show();
    return this.http
      .get(url, fileHttpOptions)
      .pipe(
        map((res: any) => {
          const fileName = this.parseFilenameFromContentDispositionV2(
            res.headers.get("content-disposition")
          );
          const fileType = res.headers.get("content-type");
          var data = res.body as Blob;

          return { fileName, fileType, data };
        }),

        finalize(() => this.loaderService.hide())
      )
      .pipe(catchError(this.handleBlobError));
  }

  handleBlobError(error: HttpErrorResponse): Promise<any> {
    if (
      error instanceof HttpErrorResponse &&
      error.error instanceof Blob &&
      error.error.type === "application/json"
    )
      return new Promise<any>((resolve, reject) => {
        let reader = new FileReader();
        reader.onload = (e: Event) => {
          try {
            const errmsg = JSON.parse((<any>e.target).result);
            reject(
              new HttpErrorResponse({
                error: errmsg,
                headers: error.headers,
                status: error.status,
                statusText: error.statusText,
                url: error.url,
              })
            );
          } catch (e) {
            reject(error);
          }
        };
        reader.onerror = (e) => {
          reject(error);
        };
        reader.readAsText(error.error);
      });
  }

  // getCached<T>(url: string, maxAgeInSeconds: number): Observable<T> {
  //  return this.storageService.getOrCreateItem<T>(url, url => this.get<T>(url), maxAgeInSeconds);
  // }

  put<T, U>(url: string, data: T): Observable<U> {
    return this.http
      .put(url, data, httpOptions)
      .pipe(map((result: any) => result as U));
  }

  post<T, U>(
    url: string,
    data: T,
    showSpinner: boolean = false
  ): Observable<U> {
    if (showSpinner) this.loaderService.show();
    return this.http.post(url, data, httpOptions).pipe(
      map((res: HttpResponse<any>) => res.body as U),
      finalize(() => {
        if (showSpinner) this.loaderService.hide();
      })
    );
  }

  getWithFileResponse(
    url: string
  ): Observable<{ fileName: string; fileType: string; data: ArrayBuffer }> {
    this.loaderService.show();
    return this.http.get(url, csvHttpOptions).pipe(
      map((res: any) => {
        const fileName = this.parseFilenameFromContentDisposition(
          res.headers.get("content-disposition")
        );
        const fileType = res.headers.get("content-type");

        var data = res.body as ArrayBuffer;
        return { fileName, fileType, data };
      }),
      finalize(() => this.loaderService.hide())
    );
  }

  postWithFileResponse<T>(
    url: string,
    data: T
  ): Observable<{ fileName: string; fileType: string; data: ArrayBuffer }> {
    this.loaderService.show();
    return this.http.post(url, data, fileHttpOptions).pipe(
      map((res: any) => {
        const fileName = this.parseFilenameFromContentDisposition(
          res.headers.get("content-disposition")
        );
        const fileType = res.headers.get("content-type");

        var data = res.body as ArrayBuffer;
        return { fileName, fileType, data };
      }),
      finalize(() => this.loaderService.hide())
    );
  }

  delete<T>(url: string): Observable<T> {
    this.loaderService.show();
    return this.http.delete(url).pipe(
      map((result: any) => result as T),
      finalize(() => this.loaderService.hide())
    );
  }

  parseFilenameFromContentDisposition(contentDisposition) {
    if (!contentDisposition) return null;

    let matches = /filename="(.*?)"/g.exec(contentDisposition);

    return matches && matches.length > 1 ? matches[1] : null;
  }
  parseFilenameFromContentDispositionV2(contentDisposition: string) {
    if (!contentDisposition) return null;

    let matches = /="(.*)"/g.exec(contentDisposition);

    return matches && matches.length > 1 ? matches[1] : null;
  }
}
